import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = 'https://api.notely.se';

type Platform = 'ios' | 'android' | 'other';

function isUrl(code: string): boolean {
  return code.startsWith('http://') || code.startsWith('https://');
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent.toLowerCase();
  if (/android/.test(ua)) return 'android';
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  return 'other';
}

const CampaignRedirect: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const didRun = useRef(false);

  useEffect(() => {
    if (!campaignId || didRun.current) return;
    didRun.current = true;

    const platform = detectPlatform();
    const storageKey = `notely_campaign_${campaignId}`;

    const run = async () => {
      const stored = localStorage.getItem(storageKey);

      if (stored) {
        try {
          const res = await fetch(
            `${API_BASE}/c/${campaignId}/verify?code=${encodeURIComponent(stored)}&platform=${platform}`
          );
          if (res.ok) {
            const data = await res.json();
            if (data.valid) {
              window.location.replace(data.redirect_url ?? (isUrl(stored) ? stored : `/code/${stored}`));
              return;
            }
          }
          // Code no longer valid — clear and fall through to claim
          localStorage.removeItem(storageKey);
        } catch {
          // Network error — use stored code optimistically
          window.location.replace(isUrl(stored) ? stored : `/code/${stored}`);
          return;
        }
      }

      // Claim a new code
      try {
        const res = await fetch(`${API_BASE}/c/${campaignId}/claim`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ platform }),
        });
        const data = await res.json();
        if (data.code) {
          localStorage.setItem(storageKey, data.code);
          window.location.replace(data.redirect_url ?? (isUrl(data.code) ? data.code : `/code/${data.code}`));
        } else if (data.fallback_url) {
          window.location.replace(data.fallback_url);
        }
      } catch {
        // Network error — nothing to do
      }
    };

    run();
  }, [campaignId]);

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-400">Getting your promo code…</p>
      </div>
    </div>
  );
};

export default CampaignRedirect;
